import { GetPanelRequestsParams } from '@/hooks/usePanelRequestsQuery'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import {
  GetPanelPartnersParams,
  GetPanelsParams,
} from '@/lib/services/panelService'
import { IntegrationService, PossibleReferralDirection } from '@/types/misc'
import {
  GetClientReferralsParams,
  GetClientsParams,
  GetCompassClientsParams,
} from '@/types/requests/clients'
import { GetCommentsRequestParams } from '@/types/requests/comments'
import {
  GetCompanyDetailsParams,
  GetPanelInviteParams,
  GetReferralFirmIndividualsParams,
  GetReferralFirmsQuery,
  GetShareableRoundRobinReferralLinksParams,
} from '@/types/requests/companies'
import {
  GetCompanyIntegrationLogParams,
  GetPlannrEmployeesParams,
} from '@/types/requests/company-integrations'
import {
  GetCompanyUsersParams,
  GetShareableIndividualReferralLinksParams,
} from '@/types/requests/company-users'
import { GetCompassAdviserStatsParams } from '@/types/requests/compass'
import {
  GetCompassReportsWithActionsParams,
  GetCompassReportsWithBusinessSaleParams,
  GetCompassReportsWithMortgageParams,
  GetCompassReportsWithPensionDetailsParams,
  GetCompassReportsWithProtectionDetailsParams,
  GetCompassReportsWithWillDetailsParams,
} from '@/types/requests/compass-reports'
import { GetDashboardStatsParams } from '@/types/requests/dashboard-stats'
import {
  GetGroupCompaniesStatsParams,
  GetGroupOverviewStatsParams,
} from '@/types/requests/groups'
import { GetInterceptorDashboardParams } from '@/types/requests/interceptor-dashboard'
import { GetGroupUserInvitesParams } from '@/types/requests/invite-group-users'
import { GetUserInvitesParams } from '@/types/requests/invite-users'
import { GetJoinCompanyRequestsParams } from '@/types/requests/join-company-requests'
import {
  GetLocationMeetingsParams,
  GetLocationUsersParams,
} from '@/types/requests/locations'
import {
  GetNetworkDashboardStatsParams,
  GetNetworkMemberFirmsParams,
  GetNetworkReferralsParams,
} from '@/types/requests/networks'
import { GetLocationOptionsParams } from '@/types/requests/operating-locations'
import { GetPanelInvitesParams } from '@/types/requests/panels'
import {
  GetPossibleReferralReviewParams,
  GetPossibleReferralsParams,
} from '@/types/requests/possible-referrals'
import { GetReferralLinksParams } from '@/types/requests/referral-links'
import {
  GetReferralInvitesRequestParams,
  GetReferralServiceAreaRequestParams,
  ReferralCollectionParams,
} from '@/types/requests/referrals'

export const QUERY_KEYS = {
  addresses: () => ['addresses'],
  affiliateLinks: (referralClientCode: string) => [
    'affiliateLinks',
    { referralClientCode },
  ],
  affiliateLinkPreferences: ['affiliate-link-preferences'],
  adminCompanyUsers: () => ['admin-company-users'],
  appState: ['app-state'],
  approvalRequests: ['approval-requests'],
  approvedPanels: () => ['approved-panels'] as const,
  availability: (
    type: string,
    owningEntityId: number | string,
    start: string,
    end: string,
    locationId: number | null,
    inPersonFormat: string | null,
    source?: string,
  ) => [
    'availability',
    { type, owningEntityId, start, end, locationId, inPersonFormat, source },
  ],
  availableRecommendedPartners: () => ['available-recommended-partners'],
  calendars: () => ['calendars'],
  calendar: (calendarId: number) => ['calendars', { calendarId }],
  calendarByUser: (userId: number) => ['calendars', { userId }],
  callbackTypes: () => ['callback-types'],
  callbackType: (callbackTypeId: number) => [
    'callback-types',
    { callbackTypeId },
  ],
  clients: (params?: GetClientsParams) =>
    params ? ['clients', params] : ['clients'],
  // Null where the screen has no client picked yet: the query is disabled, and a key built with a
  // stand-in id would put an entry for client 0 in the cache.
  client: (id: number | null) => ['client', { id }],
  clientReferralGenerateLinkFormMetadata: () => [
    'client-referral-generate-link-form-metadata',
  ],
  clientReferralFormMetadata: (clientReferralCode: string) => [
    'client-referral-form-metadata',
    { clientReferralCode },
  ],
  clientReferralHubpageSettings: () => ['client-referral-hubpage-settings'],
  clientReferralClientPageSettings: () => [
    'client-referral-client-page-settings',
  ],
  clientReferralSettingsMetadata: () => ['client-referral-settings-metadata'],
  clientReferrals: (params?: GetClientReferralsParams) =>
    params ? ['client-referrals', params] : ['client-referrals'],
  clientsByEmail: (email: string) => ['clients', { email }],
  referFriendLink: (clientId: number) => ['refer-friend-link', { clientId }],
  commercialAgreements: () => ['commercial-agreements'],
  commercialAgreement: (id: number) => ['commercial-agreements', { id }],
  commercialAgreementTemplateRevisions: () => [
    'commercial-agreement-template-revisions',
  ],
  companyAvailableServiceAreas: (companyId: number) => [
    'company-available-service-areas',
    { companyId },
  ],
  companyBankDetails: ['company-bank-details'],
  companyByReferralCode: (referralCode: string) => [
    'company-by-referral-code',
    { referralCode },
  ],
  companyCompassConfig: ['company-compass-config'],
  // No arguments matches every company's adviser stats, e.g. to refresh them
  // after invites are sent
  compassAdviserStats: (
    companyId?: number,
    params?: GetCompassAdviserStatsParams,
  ) => {
    if (companyId === undefined) {
      return ['compass-adviser-stats']
    }

    return params
      ? ['compass-adviser-stats', companyId, params]
      : ['compass-adviser-stats', companyId]
  },
  // No arguments matches every company's summary
  compassAdviserStatsSummary: (companyId?: number) =>
    companyId === undefined
      ? ['compass-adviser-stats-summary']
      : ['compass-adviser-stats-summary', companyId],
  compassClients: (params?: GetCompassClientsParams) =>
    params ? ['compass-clients', params] : ['compass-clients'],
  companyDetails: (params?: GetCompanyDetailsParams) =>
    params ? ['company-details', params] : ['company-details'],
  companyNpsFeedback: (companyId: number) => [
    'company-nps-feedback',
    { companyId },
  ],
  companyReferralFirms: (params?: GetReferralFirmsQuery) =>
    params ? ['company-referral-firms', params] : ['company-referral-firms'],
  companyOperatingLocations: (companyId: number) => [
    'company-operating-locations',
    { companyId },
  ],
  companyServices: (companyId: number) => ['company-services', { companyId }],
  compassPartners: (params?: { companyId: number }) =>
    params ? ['compass-partners', params] : ['compass-partners'],
  compassReportSharings: ['compass-report-sharings'],
  compassReportsWithActions: (params: GetCompassReportsWithActionsParams) =>
    params
      ? ['compass-reports-with-actions', params]
      : ['compass-reports-with-actions'],
  compassReportsWithBusinessSale: (
    params: GetCompassReportsWithBusinessSaleParams,
  ) =>
    params
      ? ['compass-reports-with-business-sale', params]
      : ['compass-reports-with-business-sale'],
  compassReportsWithMortgage: (params: GetCompassReportsWithMortgageParams) =>
    params
      ? ['compass-reports-with-mortgage', params]
      : ['compass-reports-with-mortgage'],
  compassReportsWithWillDetails: (
    params: GetCompassReportsWithWillDetailsParams,
  ) =>
    params
      ? ['compass-reports-with-will-details', params]
      : ['compass-reports-with-will-details'],
  compassReportsWithProtectionDetails: (
    params: GetCompassReportsWithProtectionDetailsParams,
  ) =>
    params
      ? ['compass-reports-with-protection-details', params]
      : ['compass-reports-with-protection-details'],
  compassReportsWithPensionDetails: (
    params: GetCompassReportsWithPensionDetailsParams,
  ) =>
    params
      ? ['compass-reports-with-pension-details', params]
      : ['compass-reports-with-pension-details'],
  compassReport: (token?: string) =>
    token ? ['compass-report', { token }] : ['compass-report'],
  compassSnapshot: (token?: string) =>
    token ? ['compass-snapshot', { token }] : ['compass-snapshot'],
  company: (id: number) => ['company', { id }],
  companyGlobalSearch: (searchTerm: string) => [
    'company-global-search',
    { searchTerm },
  ],
  companyNylasIntegration: () => ['company-nylas-integration'],
  companyIntegrations: () => ['company-integrations'],
  companyIntegrationByType: (type: IntegrationService) => [
    'company-integrations',
    { type },
  ],
  companyIntegrationLogs: (params?: GetCompanyIntegrationLogParams) =>
    params
      ? ['company-integration-logs', params]
      : ['company-integration-logs'],
  intellifloQueue: (params: {
    companyIntegrationId: number
    type: string
    state?: string
    searchTerm?: string
    page: number
    pageSize: number
    includeBeforeStartDate: boolean
    sortDirection?: string
  }) => ['intelliflo-queue', params],

  intellifloQueueSummary: (
    companyIntegrationId: number,
    type: string,
    // Left out to get the prefix both toggle states share, which is what invalidating wants:
    // a decision changes the counts whichever window the screen happens to be showing.
    includeBeforeStartDate?: boolean,
  ) => [
    'intelliflo-queue-summary',
    companyIntegrationId,
    type,
    ...(includeBeforeStartDate === undefined ? [] : [includeBeforeStartDate]),
  ],

  intellifloQueueReview: (
    companyIntegrationId: number,
    type: string,
    // Null while no row is open, so the disabled query's cache entry says "nothing under review"
    // instead of borrowing an id no Intelliflo record has.
    ioId: number | null,
  ) => ['intelliflo-queue-review', companyIntegrationId, type, ioId],

  intellifloOutboundReviews: (params: {
    companyIntegrationId: number
    bucket: string | null
    page: number
    pageSize: number
  }) => ['intelliflo-outbound-reviews', params],
  directLeadCaptureSettings: () => ['direct-lead-capture-settings'],
  companyProfile: (
    params?: Pick<
      typeof SEARCH_PARAMS.companyProfile,
      'identifierType' | 'identifier'
    >,
  ) => (params ? ['company-profile', params] : ['company-profile']),
  companyEmails: {
    all: ['company-emails'],
    compassInviteEmail: () => [
      QUERY_KEYS.companyEmails.all,
      { emailType: 'COMPASS_INVITE' },
    ],
  },
  companyCalendarIntegrations: () => ['company-calendar-integrations'],
  companyEmailIntegrations: () => ['company-email-integrations'],
  companySmtpDetail: (companyId: number) => [
    'company-smtp-detail',
    { companyId },
  ],
  companyUsers: (params?: GetCompanyUsersParams) =>
    params ? ['company-users', params] : ['company-users'],
  companyUser: (companyUserId: number) => ['company-user', { companyUserId }],
  companyUserIntegration: (integratedService?: IntegrationService) =>
    integratedService
      ? ['company-user-integration', integratedService]
      : ['company-user-integration'],
  companyDomainVerifications: () => ['company-domain-verifications'],
  companyUserProvisioning: () => ['company-user-provisioning'],
  companyUserProvisionByCode: (id: number) => [
    'company-user-provision-by-code',
    { id },
  ],
  dashboardStats: (params?: GetDashboardStatsParams) =>
    params ? ['dashboard-stats', params] : ['dashboard-stats'],
  directLeadCaptureFormMetadata: (companyReferralCode: string) => [
    'direct-lead-capture-form-metadata',
    { companyReferralCode },
  ],
  comments: (params?: GetCommentsRequestParams) =>
    params ? ['comments', params] : ['comments'],
  fcaServiceTypes: ['fca-service-types'],
  groupOverviewStats: (params?: GetGroupOverviewStatsParams) =>
    params ? ['group-overview-stats', params] : ['group-overview-stats'],
  groupCompaniesStats: (params?: GetGroupCompaniesStatsParams) =>
    params ? ['group-companies-stats', params] : ['group-companies-stats'],
  hubSpotAuthInit: () => ['hubspot-auth-init'],
  hubSpotHealthCheck: () => ['hubSpot-health-check'],
  hubSpotMetadata: () => ['hubspot-metadata'],
  integrationLinkedClient: (
    companyIntegrationId: number,
    integrationClientId: string,
  ) => [
    'integration-linked-client',
    { companyIntegrationId, integrationClientId },
  ],
  interceptorDashboard: (params?: GetInterceptorDashboardParams) =>
    params ? ['interceptor-dashboard', params] : ['interceptor-dashboard'],
  intellifloCampaigns: () => ['intelliflo-campaigns'],
  intellifloCampaignChannels: () => ['intelliflo-campaign-channels'],
  intellifloOpportunityStatuses: (companyIntegrationId: number) => [
    'intelliflo-opportunity-statuses',
    { companyIntegrationId },
  ],
  intellifloServiceCaseCategories: (companyIntegrationId: number) => [
    'intelliflo-service-case-categories',
    { companyIntegrationId },
  ],
  intellifloMetadata: (companyId: number) => [
    'intelliflo-metadata',
    { companyId },
  ],
  intellifloStatuses: (companyIntegrationId: number) => [
    'intelliflo-statuses',
    { companyIntegrationId },
  ],
  inviteUser: (inviteId: number, inviteCode: string) => [
    'invite-user',
    { inviteId, inviteCode },
  ],
  inviteGroupUser: (inviteId: number, inviteCode: string) => [
    'invite-group-user',
    { inviteId, inviteCode },
  ],
  invitations: (userId: number) => ['invitations', { userId }],
  isOnline: (navigatorOnlineProp: boolean) => [
    'is-online',
    { navigatorOnlineProp },
  ],
  joinCompanyRequests: (
    companyId: number,
    params?: GetJoinCompanyRequestsParams,
  ) => ['join-company-requests', companyId, params],
  latestCompassReport: ['latest-compass-report'],
  locations: () => ['locations'],
  locationCountries: () => ['location-countries'],
  location: (locationId: number) => ['locations', locationId],
  locationUsers: (locationId: number, params?: GetLocationUsersParams) =>
    params
      ? ['locations', locationId, 'users', params]
      : ['locations', locationId, 'users'],
  locationMeetings: (locationId: number, params?: GetLocationMeetingsParams) =>
    params
      ? ['locations', locationId, 'meetings', params]
      : ['locations', locationId, 'meetings'],
  locationDeletionImpact: (locationId: number) => [
    'locations',
    locationId,
    'deletion-impact',
  ],
  locationOptions: (params: GetLocationOptionsParams) => [
    'location-options',
    params,
  ],
  panelLocations: () => ['panel-locations'],
  latestCompletedCompassReport: (clientId: number) => [
    'latest-completed-compass-report',
    { clientId },
  ],
  mailingLists: (companyUserId: number) => ['mailing-lists', { companyUserId }],
  meetingTypes: () => ['meeting-types'],
  meetingType: (meetingTypeId: number) => ['meeting-types', { meetingTypeId }],
  partnerCompanies: () => ['partner-companies'],
  referralRoutingRules: () => ['referral-routing-rules'],
  referralRoutingRuleMetadata: () => ['referral-routing-rule-metadata'],
  publicMeetingType: (type: string, owningEntityId: number | string) => [
    'public-meeting-type',
    { type, owningEntityId },
  ],
  notifications: ['notifications'],
  nylasInvalidGrant: (nylasInvalidGrantId: string) => [
    'nylasInvalidGrant',
    nylasInvalidGrantId,
  ],
  nylasUsers: ['nylas-users'],
  networkOnboarding: ['network-onboarding'],
  network: (networkId: number) => ['network', networkId],
  networkDashboardStats: (
    networkId: number,
    params: GetNetworkDashboardStatsParams,
  ) => ['network-dashboard-stats', networkId, params],
  networkMemberFirms: (
    networkId: number,
    params?: GetNetworkMemberFirmsParams,
  ) =>
    params
      ? ['network-member-firms', networkId, params]
      : ['network-member-firms', networkId],
  networkReferrals: (networkId: number, params?: GetNetworkReferralsParams) =>
    params
      ? ['network-referrals', networkId, params]
      : ['network-referrals', networkId],
  onboardingChecklist: () => ['onboarding-checklist'],
  panels: (params?: GetPanelsParams) =>
    params ? ['panels', params] : ['panels'],
  panel: (panelId?: number) => (panelId ? ['panels', { panelId }] : ['panels']),
  panelPartners: (params?: GetPanelPartnersParams) =>
    params ? ['panel-partners', params] : ['panel-partners'],
  panelApprovals: (fromCompanyId: number) => [
    'panel-approvals',
    { fromCompanyId },
  ],
  panelRequests: (params?: GetPanelRequestsParams) =>
    params ? ['panel-requests', params] : ['panel-requests'],
  panelAgreements: (panelId?: number) =>
    panelId ? ['panel-agreements', { panelId }] : ['panel-agreements'],
  panelInvites: (params?: GetPanelInvitesParams) =>
    params ? ['panel-invites', params] : ['panel-invites'],
  panelInvite: (params?: GetPanelInviteParams) =>
    params ? ['panel-invite', params] : ['panel-invite'],
  panelStats: () => ['panel-stats'],
  possibleReferrals: (params?: GetPossibleReferralsParams) =>
    params ? ['possible-referrals', params] : ['possible-referrals'],
  // Nested under the `possible-referrals` prefix so invalidating that key also
  // clears the per-mailbox lists.
  mailboxPossibleReferrals: (
    scannableMailboxId: number,
    params?: GetPossibleReferralsParams,
  ) =>
    params
      ? ['possible-referrals', { scannableMailboxId }, params]
      : ['possible-referrals', { scannableMailboxId }],
  // Under the `possible-referrals` prefix, so accepting or rejecting clears it.
  possibleReferralReview: (params: GetPossibleReferralReviewParams) => [
    'possible-referrals',
    'review',
    params,
  ],
  // Not under the `possible-referrals` prefix: that prefix holds lists, which
  // the cached-list updater rewrites.
  possibleReferralReconciliation: (direction?: PossibleReferralDirection) =>
    direction
      ? ['possible-referral-reconciliation', { direction }]
      : ['possible-referral-reconciliation'],
  possibleReferralThread: (possibleReferralId: number) => [
    'possible-referral-thread',
    { possibleReferralId },
  ],
  possibleReferralCrmMatch: (possibleReferralId: number) => [
    'possible-referral-crm-match',
    { possibleReferralId },
  ],
  // Not under the match key, so re-checking the match doesn't re-run searches.
  possibleReferralNetworkUsers: (email: string) => [
    'possible-referral-network-users',
    { email },
  ],
  possibleReferralCrmMatchRecords: (
    possibleReferralId: number,
    search: string,
  ) => ['possible-referral-crm-match-records', { possibleReferralId, search }],
  scannableMailboxes: () => ['scannable-mailboxes'],
  scannableMailboxMetadata: () => ['scannable-mailbox-metadata'],
  plannrAuthInit: () => ['plannr-auth-init'],
  plannrEmployees: (params?: GetPlannrEmployeesParams) =>
    params ? ['plannr-employees', params] : ['plannr-advisers'],
  plannrIntroducers: () => ['plannr-introducers'],
  plannrHealthCheck: () => ['plannr-health-check'],
  plannrMetadata: () => ['plannr-metadata'],
  plannrStatuses: (companyIntegrationId: number) => [
    'plannr-statuses',
    { companyIntegrationId },
  ],
  plannrCaseTypes: (companyIntegrationId: number) => [
    'plannr-case-types',
    { companyIntegrationId },
  ],
  plannrCaseStatuses: (companyIntegrationId: number) => [
    'plannr-case-statuses',
    { companyIntegrationId },
  ],
  plannrCustomFields: (companyIntegrationId: number) => [
    'plannr-custom-fields',
    { companyIntegrationId },
  ],
  recommendedPartners: () => ['recommended-partners'],
  relationshipSetupPanelAgreement: (companyId: number) => [
    'relationship-setup-panel-agreement',
    { companyId },
  ],
  recommendedPartner: (companyId: number) => [
    'recommended-partners',
    { companyId },
  ],
  referClient: {
    all: ['refer-client'],
    referralFirm: (id: number) => ['referral-firm', { id }],
  },
  referralCompanyTypes: () => ['referral-company-types'],
  referralFirmIndividuals: (
    params?: GetReferralFirmIndividualsParams & { referralFirmId: number },
  ) =>
    params
      ? ['referral-firm-individuals', params]
      : ['referral-firm-individuals'],
  referralServices: (params?: GetReferralServiceAreaRequestParams) =>
    params ? ['referral-services', params] : ['referral-services'],
  referralSetupData: () => ['referral-setup-data'],
  referralWithESignatureDetails: (id: number) => [
    'referral-esignature',
    { id },
  ],
  rqCertifiedAssessment: ['rq-certified-assessment'],
  referralCommissionConsent: (id: number) => [
    'request-commission-consent',
    { id },
  ],
  referralFeedback: (id: number) => ['referral-feedback', { id }],
  referrals: (params?: ReferralCollectionParams) =>
    params ? ['referrals', params] : ['referrals'],
  referralInvite: (id: number) => ['referral-invite', { id }],
  referralItem: (id: number) => ['referrals', { id }],
  referralInvites: (params: GetReferralInvitesRequestParams) => [
    'referral-invites',
    params,
  ],
  referralLinks: (params?: GetReferralLinksParams) =>
    params ? ['referral-links', params] : ['referral-links'],
  referralLinkByIdOrCode: (idOrCode: string) => [
    'referral-link-by-id-or-code',
    { idOrCode },
  ],
  referralStats: () => ['referral-stats'],
  referralStatuses: (companyId: number | null) =>
    companyId ? ['referral-statuses', { companyId }] : ['referral-statuses'],
  recentReferrals: () => ['recentReferrals'],
  referralNotes: (id: number) => ['referral-notes', { id }],
  referralNoteMessages: (id: number) => ['referral-note-messages', { id }],
  referralTargets: (searchQuery: string, location?: string) => [
    'referralTargets',
    { searchQuery, location },
  ],
  requestCallback: (referralId: number) => ['request-callback', { referralId }],
  rescheduleMeeting: (referralId: number) => [
    'reschedule-meeting',
    { referralId },
  ],
  shareableReferralLinks: () => ['shareable-referral-links'],
  shareableIndividualReferralLinks: (
    params?: GetShareableIndividualReferralLinksParams,
  ) =>
    params
      ? ['shareable-individual-referral-links', params]
      : ['shareable-individual-referral-links'],
  shareableRoundRobinReferralLinks: (
    params?: GetShareableRoundRobinReferralLinksParams,
  ) =>
    params
      ? ['shareable-round-robin-referral-links', params]
      : ['shareable-round-robin-referral-links'],
  commercialAgreementTemplate: (agreementTemplateId: number) => [
    'commercial-agreement-template',
    { agreementTemplateId },
  ],
  userGroupInvites: (params?: GetGroupUserInvitesParams) =>
    params ? ['user-group-invites', params] : ['user-group-invites'],
  userInvites: (params?: GetUserInvitesParams) =>
    params ? ['user-invites', params] : ['user-invites'],
  xplanIntegration: ['xplan-integration'],
  xplanIntegrationMetadata: ['xplan-integration-metadata'],
  xplanUsers: ['xplan-users'],
  xplanGroups: ['xplan-groups'],
}
